'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Cookie, X, ChevronDown, ChevronUp } from 'lucide-react';

type ConsentState = 'pending' | 'accepted' | 'declined' | 'custom';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const STORAGE_KEY = 'fm_cookie_consent';

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  function saveConsent(state: ConsentState, prefs: CookiePreferences) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, prefs, timestamp: Date.now() }));
    setVisible(false);
  }

  function handleAcceptAll() {
    const all: CookiePreferences = { necessary: true, analytics: true, marketing: true };
    setPreferences(all);
    saveConsent('accepted', all);
  }

  function handleDeclineAll() {
    const minimal: CookiePreferences = { necessary: true, analytics: false, marketing: false };
    setPreferences(minimal);
    saveConsent('declined', minimal);
  }

  function handleSaveCustom() {
    saveConsent('custom', preferences);
  }

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 fade-in">
      <div className="max-w-3xl mx-auto bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Main banner */}
        <div className="p-5 md:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Cookie size={18} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-foreground text-sm mb-1">We use cookies</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                FutureMinds.ai uses cookies to operate the platform, analyse usage, and (with your consent) personalise your experience. We never sell your data.{' '}
                <Link href="/privacy-policy" className="text-primary hover:underline font-semibold">Privacy Policy</Link>
              </p>
            </div>
            <button
              onClick={handleDeclineAll}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors shrink-0"
              aria-label="Decline and close"
            >
              <X size={16} className="text-muted-foreground" />
            </button>
          </div>

          {/* Expandable details */}
          {showDetails && (
            <div className="mb-4 space-y-3 fade-in">
              {[
                { key: 'necessary' as const, label: 'Strictly Necessary', desc: 'Required for the platform to function. Cannot be disabled.', locked: true },
                { key: 'analytics' as const, label: 'Analytics', desc: 'Help us understand how students use the platform to improve it.', locked: false },
                { key: 'marketing' as const, label: 'Marketing', desc: 'Used to show relevant olympiad promotions and updates.', locked: false },
              ].map((item) => (
                <div key={item.key} className="flex items-start justify-between gap-3 p-3 bg-muted rounded-xl">
                  <div>
                    <p className="text-xs font-semibold text-foreground">{item.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                  </div>
                  <button
                    type="button"
                    disabled={item.locked}
                    onClick={() => !item.locked && setPreferences((p) => ({ ...p, [item.key]: !p[item.key] }))}
                    className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                      preferences[item.key] ? 'bg-primary' : 'bg-muted-foreground/30'
                    } ${item.locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                    aria-checked={preferences[item.key]}
                    role="switch"
                  >
                    <span
                      className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform duration-200 ${
                        preferences[item.key] ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleAcceptAll}
              className="px-5 py-2 bg-primary text-primary-foreground rounded-xl text-xs font-bold hover:bg-primary/90 transition-colors"
            >
              Accept All
            </button>
            <button
              onClick={handleDeclineAll}
              className="px-5 py-2 bg-muted text-foreground rounded-xl text-xs font-semibold hover:bg-muted/80 transition-colors border border-border"
            >
              Decline Non-Essential
            </button>
            {showDetails && (
              <button
                onClick={handleSaveCustom}
                className="px-5 py-2 bg-secondary text-white rounded-xl text-xs font-bold hover:bg-secondary/90 transition-colors"
              >
                Save Preferences
              </button>
            )}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1 px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors ml-auto"
            >
              {showDetails ? <><ChevronUp size={13} /> Less</> : <><ChevronDown size={13} /> Manage Preferences</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
